import { createFileRoute, redirect, useSearch } from '@tanstack/react-router'
import { useBreadcrumbFromRoute } from '@/hooks/use-breadcrumb-from-route'
import { ServicesSidebar, type ServicesSection } from '@/components/settings/services-sidebar'
import { CatalogSection } from '@/components/settings/organization/catalog-section'
import { InventorySettingsSection } from '@/components/settings/organization/inventory-settings-section'
import { EventTypesSection } from '@/components/settings/organization/event-types-section'
import { QuestionnaireSection } from '@/components/settings/organization/questionnaire-section'
import { DiscountsSection } from '@/components/settings/payments/discounts-section'
import { SalesTaxSection } from '@/components/settings/payments/sales-tax-section'
import { EnforcedTipsSection } from '@/components/settings/payments/enforced-tips-section'
import { QuoteTemplatesSection } from '@/components/settings/payments/quote-templates-section'
import { useCurrentOrganization } from '@/stores/organization.store'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { requirePermission, PermissionDeniedRoute } from '@/lib/require-permission-guard'

type ServicesSearch = {
  section?: ServicesSection
}

export const Route = createFileRoute('/_app/_settings/services')({
  component: ServicesPage,
  validateSearch: (search: Record<string, unknown>): ServicesSearch => {
    return {
      section: (search.section as ServicesSection) || 'catalog',
    }
  },
  beforeLoad: async ({ context, search }) => {
    // The customer-facing tip selector moved to the Client Portal page.
    if ((search.section as string | undefined) === 'tip-selector') {
      throw redirect({ to: '/client-portal', search: { section: 'tips' } })
    }
    await requirePermission(context.queryClient, 'CatalogView', 'services and pricing settings')
  },
  errorComponent: PermissionDeniedRoute,
})

function ServicesPage() {
  useBreadcrumbFromRoute()
  const search = useSearch({ from: '/_app/_settings/services' })
  const currentSection = search.section || 'catalog'
  const { currentOrganization, isLoading, refetch } = useCurrentOrganization()

  return (
    <div className="flex h-full overflow-hidden">
      <div className="scroll-fade-y flex-shrink-0 overflow-y-auto">
        <ServicesSidebar />
      </div>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="flex flex-col gap-4 p-6">
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <>
              {currentSection === 'catalog' && <CatalogSection />}
              {currentSection === 'inventory' && currentOrganization && (
                <InventorySettingsSection organization={currentOrganization} onRefetch={refetch} />
              )}
              {currentSection === 'event-types' && <EventTypesSection />}
              {currentSection === 'questionnaire' && currentOrganization && (
                <QuestionnaireSection organization={currentOrganization} onRefetch={refetch} />
              )}
              {currentSection === 'quote-templates' && <QuoteTemplatesSection />}
              {currentSection === 'discounts' && <DiscountsSection />}
              {currentSection === 'sales-tax' && <SalesTaxSection />}
              {currentSection === 'enforced-tips' && <EnforcedTipsSection />}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div>
        <SkeletonBar className="h-8 w-64 mb-2" />
        <SkeletonBar className="h-4 w-96" />
      </div>
      <Card>
        <CardHeader>
          <SkeletonBar className="h-6 w-48" />
          <SkeletonBar className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-4">
          <SkeletonBar className="h-10 w-full" />
          <SkeletonBar className="h-10 w-full" />
          <SkeletonBar className="h-10 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}
